/**
 * API module for processos (internal registered processes).
 */
import { api, type NivelDeAcesso, type UsuarioResumo } from './client';
import type { TagResumo } from './tags';

export interface ProcessoInterno {
  id: string;
  numero: string;
  tribunal: string;
  classe: string;
  status: string;
  tipo: string;
  possui_permissao_peticionar: boolean;
  created_at: string;
  /** "Acompanho de perto" do titular do acervo. */
  favorito?: boolean;
  /** Tags que quem consulta vê (PLANO_TAGS). */
  tags?: TagResumo[];
  /** O processo de um colega (visão do escritório): o nível de quem consulta e o dono. */
  acesso?: NivelDeAcesso;
  dono?: UsuarioResumo | null;
}

/**
 * Lê o processo pelo número CNJ: o do acervo, ou — na visão do escritório — o de um colega
 * que se vê (com `acesso` e `dono`; os autos, o chat e a petição agem nele, Bloco 3). 404
 * quando nenhum dos dois.
 */
export async function fetchProcessoByNumero(numero: string): Promise<ProcessoInterno> {
  const { data } = await api.get<ProcessoInterno>(`/processos/by-numero/${encodeURIComponent(numero)}`, {
    params: { origem: 'todos' },
  });
  return data;
}

/**
 * Resultado de `ensureProcessoByNumero`.
 *
 * `preparando` NÃO é erro: o processo existe, mas o serviço processual ainda
 * está montando o snapshot (202). O chamador espera o `carga.concluida` pelo
 * SSE e repete a chamada — mesmo contrato do 202 da consulta dos autos.
 */
export type EnsureProcessoResult =
  | { status: 'pronto'; processo: ProcessoInterno }
  | { status: 'preparando'; jobId: string | null; modo: string | null };

interface CargaPendenteBody {
  job_id: string | null;
  modo: string | null;
  status: string;
}

/**
 * Garante o processo no acervo, materializando-o do serviço processual se
 * ainda não estiver cadastrado (idempotente). Só para o fluxo de
 * PETICIONAMENTO — visualizar processo não inclui nada na carteira.
 */
export async function ensureProcessoByNumero(numero: string): Promise<EnsureProcessoResult> {
  // 202 já cai no range de sucesso padrão do axios — não precisa de
  // `validateStatus` (diferente da consulta dos autos, que também trata 304).
  const response = await api.post<ProcessoInterno | CargaPendenteBody>(
    `/processos/by-numero/${encodeURIComponent(numero)}`,
  );

  if (response.status === 202) {
    const body = response.data as CargaPendenteBody;
    return { status: 'preparando', jobId: body.job_id ?? null, modo: body.modo ?? null };
  }
  return { status: 'pronto', processo: response.data as ProcessoInterno };
}

export async function searchProcessos(query: string | undefined): Promise<ProcessoInterno[]> {
  const { data } = await api.get<{ items: ProcessoInterno[] }>('/processos', {
    params: query ? { query } : undefined,
  });
  return data.items;
}
